import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Loader2, Printer, Zap, Search, Download } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function QRPrintPage() {
  const navigate = useNavigate();
  const [bracelets, setBracelets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBracelets, setSelectedBracelets] = useState([]);
  const [startCode, setStartCode] = useState("");
  const [endCode, setEndCode] = useState("");
  const [qrSize, setQrSize] = useState("tiny");
  const [searchCode, setSearchCode] = useState("");
  const [searchResults, setSearchResults] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }
    fetchBracelets();
  }, [navigate]);

  const fetchBracelets = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/admin/bracelets?limit=600`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBracelets(response.data);
    } catch (error) {
      toast.error("Failed to load bracelets");
    } finally {
      setLoading(false);
    }
  };

  const searchBracelet = (query) => {
    setSearchCode(query);
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }
    const q = query.toUpperCase();
    const results = bracelets.filter(b => b.bracelet_code.includes(q)).slice(0, 10);
    setSearchResults(results);
  };

  const downloadQRImage = (bracelet) => {
    // Create a canvas with the QR code at the right size for thermal labels
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      // 203 DPI (standard thermal printer) × 0.5 inches = ~102px
      // Use 150px for slightly higher quality
      const size = 150;
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, size, size);
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(img, 0, 0, size, size);
      
      // Download
      const link = document.createElement('a');
      link.download = `${bracelet.bracelet_code}-QR.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success(`Downloaded ${bracelet.bracelet_code}-QR.png`);
    };
    img.src = `data:image/png;base64,${bracelet.qr_code_data}`;
  };

  const printSingleQR = (bracelet) => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>${bracelet.bracelet_code} - QR Code</title>
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <style>
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body { 
            font-family: -apple-system, sans-serif;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            min-height: 100vh;
            background: white;
          }
          .qr-container {
            text-align: center;
            padding: 4px;
            width: 0.5in;
          }
          .qr-container img {
            width: 0.5in;
            height: 0.5in;
            max-width: 0.5in;
            max-height: 0.5in;
            display: block;
            margin: 0 auto;
          }
          .code-label {
            font-size: 4pt;
            font-weight: 700;
            font-family: monospace;
            margin-top: 0;
            color: #000;
            line-height: 1;
          }
          .screen-preview {
            margin-top: 20px;
            text-align: center;
            border: 2px dashed #0d9488;
            padding: 20px;
            border-radius: 8px;
            background: #f0fdfa;
          }
          .screen-preview p { color: #0f766e; font-size: 13px; margin-bottom: 8px; }
          .screen-preview .big-qr {
            width: 150px;
            height: 150px;
            display: block;
            margin: 0 auto 8px;
          }
          .screen-preview .code-text {
            font-family: monospace;
            font-weight: 700;
            font-size: 14px;
            color: #0f172a;
          }
          .action-bar {
            margin-top: 20px;
            display: flex;
            gap: 10px;
            flex-wrap: wrap;
            justify-content: center;
          }
          .action-btn {
            padding: 14px 28px;
            border: none;
            border-radius: 10px;
            cursor: pointer;
            font-weight: 700;
            font-size: 16px;
          }
          .btn-print { background: #0d9488; color: white; }
          .btn-save { background: #1e40af; color: white; }
          .warning {
            background: #fef3c7;
            border: 2px solid #f59e0b;
            color: #92400e;
            padding: 12px 20px;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 600;
            margin-top: 16px;
            text-align: center;
            max-width: 400px;
          }
          .warning strong { color: #b45309; }
          .ruler {
            margin-top: 16px;
            text-align: center;
          }
          .ruler-line {
            display: inline-block;
            width: 0.5in;
            height: 2px;
            background: #ef4444;
            margin-bottom: 4px;
          }
          .ruler p { font-size: 11px; color: #64748b; }
          @media screen and (max-width: 600px) {
            .action-btn { width: 100%; margin: 0 20px; padding: 16px; }
          }
          @page {
            size: auto;
            margin: 0;
          }
          @media print {
            .no-print { display: none !important; }
            body { 
              min-height: auto;
              justify-content: flex-start;
              align-items: flex-start;
              padding: 0;
              margin: 0;
            }
            .qr-container {
              padding: 0;
              width: 0.5in;
            }
            .qr-container img {
              width: 0.5in !important;
              height: 0.5in !important;
              max-width: 0.5in !important;
              max-height: 0.5in !important;
            }
            .code-label {
              font-size: 4pt;
            }
          }
        </style>
      </head>
      <body>
        <div class="qr-container">
          <img src="data:image/png;base64,${bracelet.qr_code_data}" alt="${bracelet.bracelet_code}" />
          <div class="code-label">${bracelet.bracelet_code}</div>
        </div>
        <div class="screen-preview no-print">
          <p>Enlarged preview (prints at 1/2" actual size):</p>
          <img class="big-qr" src="data:image/png;base64,${bracelet.qr_code_data}" alt="${bracelet.bracelet_code}" />
          <div class="code-text">${bracelet.bracelet_code}</div>
        </div>
        <div class="ruler no-print">
          <div class="ruler-line"></div>
          <p>This red line = 1/2 inch (actual print size of QR code)</p>
        </div>
        <div class="action-bar no-print">
          <button class="action-btn btn-print" onclick="window.print()">Print This Sticker</button>
          <button class="action-btn btn-save" onclick="alert('Select Save as PDF in the print dialog');window.print()">Save as PDF</button>
        </div>
        <div class="warning no-print">
          IMPORTANT for correct size:<br/>
          1. Set <strong>Scale = 100%</strong> (not "Fit to Page")<br/>
          2. Set <strong>Margins = None</strong> or Minimum<br/>
          3. The QR should print at exactly 1/2" x 1/2"
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  const selectRange = () => {
    if (!startCode || !endCode) {
      toast.error("Please enter start and end codes");
      return;
    }
    
    const startNum = parseInt(startCode.replace("SFA-", ""));
    const endNum = parseInt(endCode.replace("SFA-", ""));
    
    if (isNaN(startNum) || isNaN(endNum)) {
      toast.error("Invalid bracelet codes");
      return;
    }
    
    const selected = bracelets.filter(b => {
      const num = parseInt(b.bracelet_code.replace("SFA-", ""));
      return num >= startNum && num <= endNum;
    });
    
    setSelectedBracelets(selected);
    toast.success(`Selected ${selected.length} bracelets`);
  };

  const quickPrint = () => {
    setSelectedBracelets(bracelets);
    setQrSize("tiny");
    toast.success(`Selected all ${bracelets.length} bracelets at 1/2" size`);
    // Small delay so state updates, then open print view
    setTimeout(() => {
      openPrintView(bracelets, "tiny");
    }, 100);
  };

  const openPrintView = (braceletsToUse, sizeKey) => {
    const items = braceletsToUse || selectedBracelets;
    const currentSize = sizeKey || qrSize;

    if (items.length === 0) {
      toast.error("Please select bracelets first");
      return;
    }

    const sizeMap = {
      tiny: { qr: 48, label: 6, cols: 10, mobileCols: 5, inches: 0.5 },
      bracelet: { qr: 72, label: 8, cols: 8, mobileCols: 4, inches: 0.75 },
      small: { qr: 96, label: 10, cols: 6, mobileCols: 3, inches: 1 },
      medium: { qr: 144, label: 12, cols: 4, mobileCols: 2, inches: 1.5 },
      large: { qr: 192, label: 14, cols: 3, mobileCols: 2, inches: 2 }
    };
    const size = sizeMap[currentSize];

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>QR Codes - Stress-Free Aging SOS</title>
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Manrope:wght@600;700&display=swap');
          * { box-sizing: border-box; }
          body { 
            font-family: 'Manrope', sans-serif; 
            margin: 0; 
            padding: 10px;
            background: white;
          }
          .header {
            text-align: center;
            margin-bottom: 15px;
            padding-bottom: 10px;
            border-bottom: 2px solid #0d9488;
          }
          .header h1 {
            color: #0d9488;
            margin: 0;
            font-size: 18px;
          }
          .header p {
            color: #64748b;
            margin: 5px 0 0;
            font-size: 12px;
          }
          .action-bar {
            display: flex;
            gap: 8px;
            justify-content: center;
            flex-wrap: wrap;
            margin-bottom: 15px;
            padding: 10px;
            background: #f0fdfa;
            border-radius: 8px;
          }
          .action-btn {
            padding: 10px 20px;
            border: none;
            border-radius: 8px;
            cursor: pointer;
            font-weight: 700;
            font-size: 14px;
            display: flex;
            align-items: center;
            gap: 6px;
          }
          .btn-print {
            background: #0d9488;
            color: white;
          }
          .btn-save {
            background: #1e40af;
            color: white;
          }
          .btn-share {
            background: #7c3aed;
            color: white;
          }
          .grid {
            display: grid;
            grid-template-columns: repeat(${size.cols}, 1fr);
            gap: ${currentSize === 'tiny' ? '3px' : currentSize === 'bracelet' ? '8px' : '15px'};
            justify-items: center;
          }
          .qr-card {
            text-align: center;
            padding: ${currentSize === 'tiny' ? '1px' : currentSize === 'bracelet' ? '4px' : '10px'};
            border: 1px ${currentSize === 'tiny' ? 'dotted' : 'solid'} #e2e8f0;
            border-radius: ${currentSize === 'tiny' ? '2px' : '4px'};
            background: white;
            page-break-inside: avoid;
            width: ${size.qr + (currentSize === 'tiny' ? 2 : currentSize === 'bracelet' ? 8 : 20)}px;
          }
          .qr-card img {
            width: ${size.qr}px;
            height: ${size.qr}px;
            display: block;
          }
          .qr-code-label {
            font-size: ${size.label}px;
            font-weight: 700;
            color: #0f172a;
            margin-top: ${currentSize === 'tiny' ? '0px' : currentSize === 'bracelet' ? '2px' : '6px'};
            font-family: monospace;
            ${currentSize === 'tiny' ? 'line-height: 1;' : ''}
          }
          .tips-box {
            margin-top: 20px;
            padding: 15px;
            background: #f0fdfa;
            border-radius: 8px;
            font-size: 12px;
            color: #0f766e;
          }
          .tips-box h3 {
            margin: 0 0 8px;
            color: #0d9488;
            font-size: 14px;
          }
          .tips-box ul { margin: 0; padding-left: 20px; }
          .tips-box li { margin-bottom: 4px; }
          @media screen and (max-width: 600px) {
            .grid {
              grid-template-columns: repeat(${size.mobileCols}, 1fr);
            }
            .header h1 { font-size: 15px; }
            .action-btn { padding: 12px 16px; font-size: 13px; width: 100%; justify-content: center; }
          }
          @page {
            size: auto;
            margin: 0.25in;
          }
          @media print {
            .no-print { display: none !important; }
            body { padding: 0; }
            .header { margin-bottom: 0.1in; padding-bottom: 0.05in; }
            .qr-card { 
              border: 1px dashed #ccc;
              width: auto !important;
              padding: 0.02in;
            }
            .qr-card img {
              width: ${size.inches}in !important;
              height: ${size.inches}in !important;
            }
            .qr-code-label {
              font-size: ${currentSize === 'tiny' ? '4pt' : currentSize === 'bracelet' ? '5pt' : '6pt'};
            }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Stress-Free Aging SOS - QR Codes</h1>
          <p>${items.length} codes | Size: ${currentSize === 'tiny' ? '1/2 inch (for bracelet plates)' : currentSize === 'bracelet' ? '3/4 inch (for bracelet metal plate)' : currentSize}</p>
        </div>
        <div class="action-bar no-print">
          <button class="action-btn btn-print" onclick="window.print()">
            Print Now
          </button>
          <button class="action-btn btn-save" onclick="savePDF()">
            Save as PDF
          </button>
        </div>
        <div style="background:#fef3c7;border:2px solid #f59e0b;color:#92400e;padding:10px 16px;border-radius:8px;font-size:13px;font-weight:600;text-align:center;margin-bottom:12px;" class="no-print">
          IMPORTANT: Set <strong style="color:#b45309">Scale = 100%</strong> in print settings (not "Fit to Page") so QR codes print at exact ${currentSize === 'tiny' ? '1/2' : currentSize === 'bracelet' ? '3/4' : size.inches}" size
        </div>
        <div class="grid">
          ${items.map(b => `
            <div class="qr-card">
              <img src="data:image/png;base64,${b.qr_code_data}" alt="${b.bracelet_code}" />
              <div class="qr-code-label">${b.bracelet_code}</div>
            </div>
          `).join('')}
        </div>
        <div class="tips-box no-print">
          <h3>Tips for Thermal Printers & Silver Labels</h3>
          <ul>
            <li><strong>Thermal printer:</strong> Connect via Bluetooth, select this page, and print at 100% scale</li>
            <li><strong>Save as PDF first</strong> if your thermal printer app works better with files</li>
            <li><strong>Silver/waterproof labels:</strong> Use highest quality setting for sharp QR edges</li>
            <li><strong>Test scan one QR code</strong> after printing to make sure it reads before doing the full batch</li>
            <li><strong>On iPhone:</strong> Tap "Share" then "Save to Files" or "Print" to send to your thermal printer</li>
            <li><strong>On Android:</strong> Tap menu then "Print" or "Share" to send to your printer app</li>
          </ul>
        </div>
        <script>
          function savePDF() {
            // Trigger browser's Save as PDF via print dialog
            alert('In the print dialog, select "Save as PDF" as the destination instead of a printer. This saves the QR codes as a file you can print later from any device.');
            window.print();
          }
          function shareQR() {
            if (navigator.share) {
              navigator.share({
                title: 'QR Codes - Stress-Free Aging SOS',
                text: '${items.length} QR codes for bracelets',
                url: window.location.href
              }).catch(() => {});
            }
          }
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  const printQRCodes = () => openPrintView();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-teal-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="container-app flex items-center justify-between h-16">
          <div className="flex items-center gap-4">
            <Link to="/dashboard">
              <Button variant="ghost" size="icon">
                <ArrowLeft className="h-5 w-5" />
              </Button>
            </Link>
            <div className="flex items-center gap-2">
              <Printer className="h-6 w-6 text-teal-600" />
              <span className="font-bold text-xl text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Print QR Codes
              </span>
            </div>
          </div>
          <Link to="/dashboard" className="flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_bracelet-alert/artifacts/suao9dvz_SFA%20Logo%20-%20long.png" 
              alt="Stress-Free Aging" 
              className="h-10 w-auto"
            />
          </Link>
        </div>
      </header>

      <main className="container-app py-8">
        <div className="max-w-4xl mx-auto">
          {/* Print Single QR - For Fair/Event Use */}
          <Card className="border-teal-400 bg-white mb-8 shadow-lg">
            <CardHeader className="bg-gradient-to-r from-teal-600 to-teal-700 rounded-t-lg">
              <CardTitle className="text-white flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <Printer className="h-5 w-5" />
                Print Single QR Code
              </CardTitle>
              <CardDescription className="text-teal-100">
                Search for a bracelet code. Use <strong>Save</strong> for FlashLabel Pro / thermal printers, or <strong>Print</strong> for regular printers.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Type bracelet code (e.g. SFA-00001)"
                  value={searchCode}
                  onChange={(e) => searchBracelet(e.target.value)}
                  className="pl-10 font-mono text-lg h-12"
                  data-testid="single-search-input"
                />
              </div>
              {searchResults.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {searchResults.map((b) => (
                    <div
                      key={b.id}
                      className="flex items-center gap-3 p-3 border border-slate-200 rounded-lg hover:border-teal-400 hover:bg-teal-50 transition-colors"
                    >
                      <img
                        src={`data:image/png;base64,${b.qr_code_data}`}
                        alt={b.bracelet_code}
                        className="w-14 h-14 object-contain flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-mono font-bold text-slate-900">{b.bracelet_code}</p>
                        <p className="text-xs text-slate-500">
                          {b.is_registered ? "Registered" : "Available"}
                        </p>
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        <Button
                          onClick={() => downloadQRImage(b)}
                          size="sm"
                          className="bg-blue-600 hover:bg-blue-700"
                          data-testid={`download-single-${b.bracelet_code}`}
                        >
                          <Download className="h-4 w-4 mr-1" />
                          Save
                        </Button>
                        <Button
                          onClick={() => printSingleQR(b)}
                          size="sm"
                          className="bg-teal-600 hover:bg-teal-700"
                          data-testid={`print-single-${b.bracelet_code}`}
                        >
                          <Printer className="h-4 w-4 mr-1" />
                          Print
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {searchCode && searchResults.length === 0 && (
                <p className="text-sm text-slate-500 text-center py-3">No bracelets found matching "{searchCode}"</p>
              )}
            </CardContent>
          </Card>

          {/* Bulk Print Banner */}
          <Card className="border-teal-300 bg-gradient-to-r from-teal-600 to-teal-700 mb-8">
            <CardContent className="py-5">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="text-white text-center sm:text-left">
                  <h3 className="text-base font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Need to print in bulk?
                  </h3>
                  <p className="text-teal-100 text-sm mt-1">
                    Print all {bracelets.length} QR codes at once for pre-cutting sticker sheets.
                  </p>
                </div>
                <Button
                  onClick={quickPrint}
                  className="bg-white text-teal-700 hover:bg-teal-50 font-bold px-6 py-2 whitespace-nowrap"
                  data-testid="quick-print-btn"
                >
                  <Zap className="h-4 w-4 mr-2" />
                  Quick Print All
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Instructions Card */}
          <Card className="border-teal-200 bg-teal-50 mb-8">
            <CardHeader>
              <CardTitle className="text-teal-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                How to Add QR Codes to Your Bracelets
              </CardTitle>
            </CardHeader>
            <CardContent className="text-teal-700 space-y-4">
              <div>
                <p className="font-semibold mb-2">Recommended Waterproof Paper:</p>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li><strong>Vinyl Sticker Paper</strong> - Best overall. Brands: Online Labels, Avery 4395, Zicoto (~$15-20/25 sheets)</li>
                  <li><strong>Polyester Laser Labels</strong> - Avery 5520 (silver) or 5523 (white) - very durable</li>
                  <li><strong>Clear Waterproof Labels</strong> - PPD Inkjet - shows metal through</li>
                  <li><strong>Silver Metallic Labels</strong> - Looks professional on bracelet metal plates</li>
                </ul>
              </div>
              <div>
                <p className="font-semibold mb-2">Printing Tips:</p>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>Print at <strong>highest quality setting</strong> for sharp QR codes</li>
                  <li>Use <strong>100% scale</strong> - don't use "fit to page"</li>
                  <li>Let dry 5-10 minutes before cutting</li>
                  <li>Optional: Add clear laminate overlay for extra durability</li>
                </ul>
              </div>
              <div className="pt-2 border-t border-teal-200">
                <p className="font-semibold mb-2">Using a Thermal Printer from Your Phone:</p>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>Tap <strong>"Quick Print All"</strong> above to open QR codes in a new page</li>
                  <li>Tap <strong>"Print Now"</strong> to send directly to your Bluetooth thermal printer</li>
                  <li>Or tap <strong>"Save as PDF"</strong> to save the file and print later</li>
                  <li><strong>Test scan one code</strong> after printing to verify before doing the whole batch</li>
                </ul>
              </div>
              <div className="pt-2 border-t border-teal-200">
                <p className="text-sm"><strong>Amazon search:</strong> "Waterproof vinyl sticker paper" or "Silver metallic waterproof labels"</p>
              </div>
            </CardContent>
          </Card>

          {/* Selection Card */}
          <Card className="border-slate-200 mb-8">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Select Bracelets to Print</CardTitle>
              <CardDescription>Choose a range of bracelet codes to generate printable QR codes</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Start Code</Label>
                  <Input
                    placeholder="SFA-00001"
                    value={startCode}
                    onChange={(e) => setStartCode(e.target.value.toUpperCase())}
                    className="font-mono"
                    data-testid="start-code-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>End Code</Label>
                  <Input
                    placeholder="SFA-00050"
                    value={endCode}
                    onChange={(e) => setEndCode(e.target.value.toUpperCase())}
                    className="font-mono"
                    data-testid="end-code-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>QR Size</Label>
                  <Select value={qrSize} onValueChange={setQrSize}>
                    <SelectTrigger data-testid="qr-size-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="tiny">Bracelet Small (1/2 inch)</SelectItem>
                      <SelectItem value="bracelet">Bracelet (3/4 inch)</SelectItem>
                      <SelectItem value="small">Small (1 inch)</SelectItem>
                      <SelectItem value="medium">Medium (1.5 inch)</SelectItem>
                      <SelectItem value="large">Large (2 inch)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex flex-wrap gap-3">
                <Button onClick={selectRange} variant="outline" data-testid="select-range-btn">
                  Select Range
                </Button>
                <Button 
                  onClick={() => {
                    setSelectedBracelets(bracelets.slice(0, 50));
                    toast.success("Selected first 50 bracelets");
                  }}
                  variant="outline"
                >
                  Select First 50
                </Button>
                <Button 
                  onClick={() => {
                    setSelectedBracelets(bracelets);
                    toast.success(`Selected all ${bracelets.length} bracelets`);
                  }}
                  variant="outline"
                >
                  Select All ({bracelets.length})
                </Button>
              </div>

              {selectedBracelets.length > 0 && (
                <div className="p-4 bg-slate-100 rounded-lg">
                  <p className="font-medium text-slate-900 mb-3">
                    {selectedBracelets.length} bracelets selected
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Button onClick={printQRCodes} className="bg-teal-600 hover:bg-teal-700" data-testid="print-qr-btn">
                      <Printer className="h-4 w-4 mr-2" />
                      Open Print View
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Preview Grid */}
          {selectedBracelets.length > 0 && (
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Preview (First 12)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {selectedBracelets.slice(0, 12).map((b) => (
                    <div key={b.id} className="text-center p-3 bg-white border border-slate-200 rounded-lg">
                      <img
                        src={`data:image/png;base64,${b.qr_code_data}`}
                        alt={b.bracelet_code}
                        className="w-full aspect-square object-contain mb-2"
                      />
                      <p className="font-mono text-xs font-semibold">{b.bracelet_code}</p>
                    </div>
                  ))}
                </div>
                {selectedBracelets.length > 12 && (
                  <p className="text-center text-slate-500 mt-4">
                    +{selectedBracelets.length - 12} more in print view
                  </p>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
}
